import { Anchor } from 'src/design/atoms/Anchor';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { formatDate } from 'src/utils';
import { Link } from 'react-router-dom';
import { JobDto, WorkTicketDto } from 'src/api/FiretrolApiClient';
import { useWorkTickets } from 'src/api/queries/workTickets/useWorkTickets';
import { useAuth } from 'src/hooks/useAuth';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { WorkTicketForm } from 'src/routes/Jobs/Tickets/WorkTicketForm';

interface ITicketsTabProps {
  initialTickets: WorkTicketDto[];
  onChange: (tickets: WorkTicketDto[]) => void;
  dailyId?: string;
  selectedJob?: JobDto | null;
  disabled?: boolean;
}

export const TicketsTab: React.FC<ITicketsTabProps> = ({
  initialTickets,
  onChange,
  dailyId,
  selectedJob,
  disabled = false,
}) => {
  const [showForm, setShowForm] = React.useState(false);
  const { user } = useAuth();

  const { data: jobTickets } = useWorkTickets(selectedJob?.id);

  // The job's ticket list is the source of truth; this tab shows the slice
  // belonging to the daily, so a ticket saved anywhere shows up here.
  const tickets = React.useMemo(() => {
    if (!jobTickets) return initialTickets;
    const dailyTicketIds = new Set(initialTickets.map((t) => t.id));
    return jobTickets.filter(
      (t) => dailyTicketIds.has(t.id) || (!!dailyId && t.dailyId === dailyId),
    );
  }, [jobTickets, initialTickets, dailyId]);

  const ticketIds = tickets.map((t) => t.id).join(',');
  const ticketsRef = React.useRef(tickets);
  ticketsRef.current = tickets;

  React.useEffect(() => {
    onChange(ticketsRef.current);
    // onChange writes back into the list this tab reads, so it only runs when
    // the membership itself changed.
  }, [ticketIds]);

  if (!selectedJob || !user) return null;

  const renderTickets = () => {
    return tickets.map((t) => {
      return (
        <Paper key={t.id}>
          <Stack gap="xs">
            <Text>
              <Text component="span" fw={500}>
                Requested By:
              </Text>
              {t.requester.length > 0 ? t.requester : 'N/A'}
            </Text>
            <Text>
              <Text component="span" fw={500}>
                Date Requested:
              </Text>
              {formatDate(t.date)}
            </Text>
            <Text>
              <Text component="span" fw={500}>
                Status:
              </Text>
              {t.status}
            </Text>
            <Anchor
              component={Link}
              to={`/jobs/${t.jobId}/tickets/view/${t.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Work Ticket
            </Anchor>
          </Stack>
        </Paper>
      );
    });
  };

  const initialValues: WorkTicketDto = {
    id: '00000000-0000-0000-0000-000000000000',
    createdById: user.id,
    useSignaturePad: false,
    date: new Date(),
    summary: '',
    signatureString: '',
    requester: '',
    signature: '',
    status: 'Created',
    rateRequests: [],
    jobId: selectedJob.id,
    attachment: [],
    attachmentsToAdd: [],
    attachmentsToRemove: [],
    dailyId: dailyId,
  };

  return (
    <Stack gap="md">
      {showForm ? (
        <WorkTicketForm
          jobId={selectedJob.id}
          initialValues={initialValues}
          cancelButton={
            <BasicButton variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </BasicButton>
          }
          sideCancelButton={
            <BasicButton
              fullWidth
              variant="outline"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </BasicButton>
          }
          onTicketCreated={() => setShowForm(false)}
        />
      ) : (
        <>
          {!disabled && (
            <CreateButton onClick={() => setShowForm(true)}>
              Create Ticket
            </CreateButton>
          )}
          <Stack gap="md">{renderTickets()}</Stack>
        </>
      )}
    </Stack>
  );
};
